import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Comic } from '@/types/comic';
import { categoryName, formatCount } from '@/utils/comic';

export default function HeroCarousel({ comics }: { comics: Comic[] }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (comics.length <= 1) return;
    const timer = setInterval(() => {
      setIndex((i) => (i + 1) % comics.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [comics.length]);

  if (comics.length === 0) return null;
  const active = comics[index];

  return (
    <section className="relative h-[440px] w-full overflow-hidden md:h-[600px]">
      {comics.map((c, i) => (
        <div
          key={c.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={c.banner}
            alt={`${c.title} 专题推荐大图`}
            title={`${c.title} 热门韩漫推荐`}
            className="h-full w-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/85 via-foreground-950/55 to-foreground-950/15" />
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full max-w-7xl items-center px-4 md:px-6">
        <div className="max-w-xl animate-fade-up">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-foreground-950">
              专题推荐
            </span>
            {active.categories.map((k) => (
              <span
                key={k}
                className="rounded-full border border-background-50/40 px-3 py-1 text-xs text-background-100"
              >
                {categoryName(k)}
              </span>
            ))}
          </div>
          <h2 className="text-2xl font-bold leading-tight text-background-50 md:text-4xl">
            {active.title}
          </h2>
          <p className="mt-2 text-sm text-background-200 md:text-base">
            作者：{active.author} · {active.status} · 共 {active.chapterCount} 话
          </p>
          <p className="mt-4 line-clamp-3 max-w-lg text-sm leading-relaxed text-background-200 md:text-base">
            {active.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/read/${active.id}/ch-${active.chapterCount}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-play-fill" />
              开始阅读
            </Link>
            <Link
              to={`/detail/${active.id}`}
              className="whitespace-nowrap rounded-md border border-background-100/50 bg-background-50/10 px-5 py-2.5 text-sm font-medium text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 cursor-pointer"
            >
              查看详情
            </Link>
            <span className="flex items-center gap-1 text-xs text-background-300">
              <i className="ri-fire-line text-accent-400" />
              {formatCount(active.views)} 人气
            </span>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setIndex((i) => (i - 1 + comics.length) % comics.length)}
        className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/60 md:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-xl" />
      </button>
      <button
        type="button"
        onClick={() => setIndex((i) => (i + 1) % comics.length)}
        className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground-950/40 text-background-50 backdrop-blur transition-colors hover:bg-foreground-950/60 md:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {comics.map((c, i) => (
          <button
            key={c.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`h-1.5 rounded-full transition-all ${
              i === index ? 'w-6 bg-accent-500' : 'w-1.5 bg-background-50/60'
            }`}
            aria-label={`切换到第 ${i + 1} 张`}
          />
        ))}
      </div>
    </section>
  );
}